import { createColumnHelper } from "@tanstack/react-table"
import { format, parseISO } from "date-fns"

import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { type IncentiveListRow, type IncentiveStatus } from "@/hooks/useIncentives"

const columnHelper = createColumnHelper<IncentiveListRow>()

export const STATUS_LABELS: Record<IncentiveStatus, string> = {
  draft: "Draft",
  live: "Live",
  ended: "Ended",
  cancelled: "Cancelled",
}

export const STATUS_CLASSES: Record<IncentiveStatus, string> = {
  draft: "bg-palette-honeycomb100 text-palette-honeycomb800",
  live: "bg-delivered text-indicators-green",
  ended: "bg-palette-jungleGreen000 text-palette-jungleGreen600",
  cancelled: "bg-palette-angry100 text-indicators-red",
}

const formatDay = (date: string) => format(parseISO(date), "d MMM yyyy")

const pounds = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })

// Not a component: a component export here trips react-refresh, which wants this file to export
// only column definitions.
const muted = (text: string) => <span className="text-palette-jungleGreen500">{text}</span>

// The first value and a count keeps every row one line high; the tooltip lists them all.
const firstAndCount = (values: string[]) => {
  if (values.length === 0) return muted("All")
  if (values.length === 1) return values[0]

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          {values[0]} <span className="text-palette-jungleGreen500">+{values.length - 1}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {values.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

export const incentiveColumns = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: ({ getValue }) => (
      <span className="font-medium text-palette-jungleGreen900">{getValue()}</span>
    ),
  }),
  columnHelper.accessor("depots", {
    header: "Depot",
    cell: ({ getValue }) => firstAndCount(getValue()),
  }),
  columnHelper.accessor("driver_types", {
    header: "Driver type",
    cell: ({ getValue }) => firstAndCount(getValue()),
  }),
  columnHelper.display({
    id: "dates",
    header: "Dates",
    cell: ({ row: { original: incentive } }) =>
      incentive.end_date
        ? `${formatDay(incentive.start_date)} - ${formatDay(incentive.end_date)}`
        : `From ${formatDay(incentive.start_date)}`,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: ({ getValue }) => (
      <Badge className={`pointer-events-none border-transparent ${STATUS_CLASSES[getValue()]}`}>
        {STATUS_LABELS[getValue()]}
      </Badge>
    ),
  }),
  columnHelper.accessor("app_comms", {
    header: "App Comms",
    cell: ({ getValue }) => {
      const appComms = getValue()
      if (!appComms) return muted("-")
      return appComms.show_homepage_banner ? "Shifts page and banner" : "Shifts page"
    },
  }),
  // A dash rather than zero, so an incentive never reconciled doesn't read as one that paid nobody.
  columnHelper.display({
    id: "drivers_paid",
    header: "Drivers paid last reconcile",
    cell: ({ row: { original: incentive } }) =>
      incentive.latest_period ? incentive.latest_period.drivers_paid : muted("-"),
  }),
  columnHelper.display({
    id: "total_paid",
    header: "Total paid",
    cell: ({ row: { original: incentive } }) =>
      incentive.latest_period ? pounds.format(incentive.latest_period.total_paid) : muted("-"),
  }),
]
